/**
 * Pick the rendering for a module's / interface's widget config.
 *
 * Shared by WidgetNode (board), SpaceRenderer, and TypesMode previews — kept
 * out of the component file so all three resolve widgets identically.
 */
import {
  extractWidgetSpec,
  type WidgetSpec,
} from "@archlang/scene/arch-model";
import type { Module } from "@archlang/scene/arch-model";
import { BUILTIN_MODULE_TAG, sceneRenderTag } from "./builtinWidgets";

/**
 * Every module renders through a widget — when no explicit `widget:` field is
 * configured, the universal `arch-module` fallback (registered by
 * builtinWidgets.ts at app boot) takes over. Returns:
 *   - { kind: "tag", ... }      explicit custom-element tag, OR fallback
 *   - { kind: "template", ... } when `widget:` is a string (inline HTML)
 */
export type WidgetRender =
  | { readonly kind: "tag"; readonly tag: string; readonly spec: WidgetSpec }
  | { readonly kind: "template"; readonly template: string; readonly spec: WidgetSpec };

export function widgetForModule(module: Module): WidgetRender {
  const spec = extractWidgetSpec(module);
  // Shape widgets (database cylinder, table) render through the scene element
  // so the live board and the SVG export share one composition. The stdlib
  // tag stays in `spec` (the composer reads it to pick the shape); only the
  // rendered element tag is swapped.
  if (spec.tag) return { kind: "tag", tag: sceneRenderTag(spec.tag), spec };
  if (spec.template) return { kind: "template", template: spec.template, spec };
  return { kind: "tag", tag: BUILTIN_MODULE_TAG, spec };
}

/**
 * Interfaces use the same `widget.<...>` cascade fields as modules —
 * `widget: <tag>` mounts a registered custom element, `widget: "<html>"`
 * renders an inline template. Returns null when no widget is configured
 * (caller decides whether to fall back to a generic arrow line).
 */
export function widgetForInterface(
  iface: { fields: readonly { path: readonly string[]; value: unknown }[] },
): WidgetRender | null {
  const spec = extractWidgetSpec(iface);
  if (spec.tag) return { kind: "tag", tag: spec.tag, spec };
  if (spec.template) return { kind: "template", template: spec.template, spec };
  return null;
}
